import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { XCircleIcon } from '@heroicons/react/24/outline'

function DeleteBlockedModal({
  isOpen,
  onClose,
  onCancelSubscription,
  account,
  subscriptionPlan = 'starter'
}) {
  const { t } = useTranslation('settings')
  const [redirecting, setRedirecting] = useState(false)

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen || !account) return null

  const handleCancelSubscription = async () => {
    setRedirecting(true)
    try {
      await onCancelSubscription(account.id)
    } catch (error) {
      console.error('Failed to redirect to subscription cancellation:', error)
      setRedirecting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200"
        role="dialog"
        aria-labelledby="delete-blocked-modal-title"
        aria-describedby="delete-blocked-modal-description"
      >
        <div className="p-6">
          {/* Icon */}
          <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-red-100 mb-4">
            <XCircleIcon className="h-7 w-7 text-red-600" aria-hidden="true" />
          </div>

          {/* Title */}
          <h3
            id="delete-blocked-modal-title"
            className="text-lg font-semibold text-gray-900 text-center mb-2"
          >
            {t('accounts.deleteBlocked.modal.title')}
          </h3>

          {/* Account info */}
          <div className="flex items-center justify-center space-x-3 mb-4">
            {account.profile_picture_url && (
              <img
                src={account.profile_picture_url}
                alt={account.username}
                className="w-10 h-10 rounded-full"
              />
            )}
            <span className="font-medium text-gray-900">@{account.username}</span>
          </div>

          {/* Description */}
          <div id="delete-blocked-modal-description" className="space-y-3">
            <p className="text-sm text-gray-600 text-center">
              {t('accounts.deleteBlocked.modal.description')}
            </p>

            {/* Reason box */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-medium text-red-900 mb-1">
                {t('accounts.deleteBlocked.modal.reason.title')}
              </p>
              <p className="text-sm text-red-800">
                {t('accounts.deleteBlocked.modal.reason.activeSubscription', { plan: subscriptionPlan.toUpperCase() })}
              </p>
            </div>

            {/* Instructions */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-900 mb-2">
                {t('accounts.deleteBlocked.modal.instructions.title')}
              </p>
              <ol className="space-y-1 text-sm text-gray-700 list-decimal list-inside">
                <li>{t('accounts.deleteBlocked.modal.instructions.step1')}</li>
                <li>{t('accounts.deleteBlocked.modal.instructions.step2')}</li>
                <li>{t('accounts.deleteBlocked.modal.instructions.step3')}</li>
              </ol>
            </div>

            <p className="text-xs text-gray-500 text-center">
              {t('accounts.deleteBlocked.modal.note')}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            {t('accounts.deleteBlocked.modal.button.goBack')}
          </button>
          <button
            onClick={handleCancelSubscription}
            disabled={redirecting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center space-x-2"
          >
            {redirecting && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            <span>
              {redirecting
                ? t('accounts.deleteBlocked.modal.button.redirecting')
                : t('accounts.deleteBlocked.modal.button.cancelSubscription')
              }
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}

DeleteBlockedModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCancelSubscription: PropTypes.func.isRequired,
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
    profile_picture_url: PropTypes.string
  }),
  subscriptionPlan: PropTypes.string
}

export default DeleteBlockedModal